import React from 'react';
import { Alert, Button, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useViewerStore } from '../../../../../store/store';
import { useCurrentUser } from '../../../../../contexts/UserContext';
import type { DashboardSectionPanelId } from '../dashboardSectionNav';
import { DashboardMainCardsSection } from './DashboardMainCardsSection';
import { DashboardAuxiliarySection } from './DashboardAuxiliarySection';
import { ObdGoogleStreetViewProvider } from '../ObdGoogleStreetViewProvider';
import {
    DEFAULT_SITE_VIEWER_MESSAGE,
    SITE_VIEWER_UNAVAILABLE_TITLE,
    useSiteViewer3dBlocked,
} from '../../../../../helpers/siteViewer3dAccess';

interface DashboardReadyLayoutSectionProps {
    theme: 'dark' | 'light';
    siteAddress: any;
    titleSearchData: any;
    oppRefreshKey: number;
    setAddTitleContactsModalOpen: (next: boolean) => void;
    handleOpenBlackKnightLeadModal: () => void;
    dashboardAnnualUsage: number | null;
    dashboardSystemSizeKw: number | null;
    dashboardAvgDailyUsage: number | null;
    dashboardMultiplier: number | null;
    triggerPricingGridForDesign: (design: any) => void;
    priceQuote: any;
    isDashboardOpen: boolean;
    setIsDashboardOpen: (next: boolean | ((prev: boolean) => boolean)) => void;
    pricingFetchTrigger: number | null;
    pricingDesignForTrigger: any;
    setPricingFetchTrigger: React.Dispatch<React.SetStateAction<number | null>>;
    opportunityDetails: any;
    selectedDesignForPricing: any;
    setPricingGridModalOpen: (next: boolean) => void;
    opportunityID: any;
    financePartnerSyncModalOpen: boolean;
    setFinancePartnerSyncModalOpen: (next: boolean) => void;
    leadSyncFormOpen: boolean;
    setLeadSyncFormOpen: (next: boolean) => void;
    initialDashboardReady: boolean;
    dashboardSectionPanel: DashboardSectionPanelId;
    setDashboardSectionPanel: React.Dispatch<React.SetStateAction<DashboardSectionPanelId>>;
    designsDashboardSlot: React.ReactNode;
    ahjTown: string;
    setbackNotes: string[];
    setAhjModalOpen: (next: boolean) => void;
    setOppValidationModalOpen: (next: boolean) => void;
    aiLoading?: boolean;
}

export const DashboardReadyLayoutSection: React.FC<DashboardReadyLayoutSectionProps> = (props) => {
    const {
        theme,
        siteAddress,
        titleSearchData,
        oppRefreshKey,
        setAddTitleContactsModalOpen,
        handleOpenBlackKnightLeadModal,
        dashboardAnnualUsage,
        dashboardSystemSizeKw,
        dashboardAvgDailyUsage,
        dashboardMultiplier,
        triggerPricingGridForDesign,
        priceQuote,
        isDashboardOpen,
        setIsDashboardOpen,
        pricingFetchTrigger,
        pricingDesignForTrigger,
        setPricingFetchTrigger,
        opportunityDetails,
        selectedDesignForPricing,
        setPricingGridModalOpen,
        opportunityID,
        financePartnerSyncModalOpen,
        setFinancePartnerSyncModalOpen,
        leadSyncFormOpen,
        setLeadSyncFormOpen,
        initialDashboardReady,
        dashboardSectionPanel,
        setDashboardSectionPanel,
        designsDashboardSlot,
        ahjTown,
        setbackNotes,
        setAhjModalOpen,
        setOppValidationModalOpen,
    } = props;

    const { blocked: siteViewerUnavailable, message: siteViewerMessage } = useSiteViewer3dBlocked();
    const dashboardLayoutVariant = useViewerStore((s) => s.dashboardLayoutVariant);
    const navigate = useNavigate();
    const { hasCapability } = useCurrentUser();
    const canOpenSitesTable = hasCapability('Sites_Table');
    const hasFloatingSectionRail =
        initialDashboardReady &&
        isDashboardOpen &&
        dashboardLayoutVariant !== 'v3' &&
        hasCapability('Show_OneButton');

    const handleReloadSite = () => {
        window.location.reload();
    };

    const handleGoToSitesTable = () => {
        useViewerStore.setState({ siteLoadingError: null });
        navigate('/sites');
    };

    return (
        <ObdGoogleStreetViewProvider theme={theme}>
            <>
            {siteViewerUnavailable ? (
                <aside
                    className={`obd-site-load-error-banner obd-theme-${theme}${
                        hasFloatingSectionRail ? ' obd-site-load-error-banner--rail-offset' : ''
                    }`}
                    aria-label={SITE_VIEWER_UNAVAILABLE_TITLE}
                >
                    <Alert
                        className="obd-site-load-error-banner__alert"
                        type="warning"
                        showIcon
                        closable={false}
                        message={SITE_VIEWER_UNAVAILABLE_TITLE}
                        description={
                            <div className="obd-site-load-error-banner__body">
                                <span className="obd-site-load-error-banner__message">
                                    {siteViewerMessage || DEFAULT_SITE_VIEWER_MESSAGE}
                                </span>
                                <Space className="obd-site-load-error-banner__actions" wrap>
                                    <Button type="primary" onClick={handleReloadSite}>
                                        Reload site
                                    </Button>
                                    {canOpenSitesTable ? (
                                        <Button onClick={handleGoToSitesTable}>
                                            Back to Sites
                                        </Button>
                                    ) : null}
                                </Space>
                            </div>
                        }
                    />
                </aside>
            ) : null}
            <DashboardMainCardsSection
                theme={theme}
                siteAddress={siteAddress}
                titleSearchData={titleSearchData}
                oppRefreshKey={oppRefreshKey}
                setAddTitleContactsModalOpen={setAddTitleContactsModalOpen}
                handleOpenBlackKnightLeadModal={handleOpenBlackKnightLeadModal}
                dashboardAnnualUsage={dashboardAnnualUsage}
                dashboardSystemSizeKw={dashboardSystemSizeKw}
                dashboardAvgDailyUsage={dashboardAvgDailyUsage}
                dashboardMultiplier={dashboardMultiplier}
                triggerPricingGridForDesign={triggerPricingGridForDesign}
                priceQuote={priceQuote}
                isDashboardOpen={isDashboardOpen}
                pricingFetchTrigger={pricingFetchTrigger}
                pricingDesignForTrigger={pricingDesignForTrigger}
                setPricingFetchTrigger={setPricingFetchTrigger}
                opportunityDetails={opportunityDetails}
                selectedDesignForPricing={selectedDesignForPricing}
                setPricingGridModalOpen={setPricingGridModalOpen}
                opportunityID={opportunityID}
                dashboardSectionPanel={dashboardSectionPanel}
                setDashboardSectionPanel={setDashboardSectionPanel}
                designsDashboardSlot={designsDashboardSlot}
                ahjTown={ahjTown}
                setbackNotes={setbackNotes}
                setAhjModalOpen={setAhjModalOpen}
                setOppValidationModalOpen={setOppValidationModalOpen}
                setIsDashboardOpen={setIsDashboardOpen}
                initialDashboardReady={initialDashboardReady}
            />

            <DashboardAuxiliarySection
                financePartnerSyncModalOpen={financePartnerSyncModalOpen}
                setFinancePartnerSyncModalOpen={setFinancePartnerSyncModalOpen}
                leadSyncFormOpen={leadSyncFormOpen}
                setLeadSyncFormOpen={setLeadSyncFormOpen}
            />
            </>
        </ObdGoogleStreetViewProvider>
    );
};
